// Re-export types for backward compatibility
export * from './types';

// Import localStorage-only services
import * as studentsFileStorageService from './students/studentsFileStorageService';
import { teachersFileStorageService } from './teachers/teachersFileStorageService';
import { adminFileStorageService } from './admins/fileStorageService';
import { timetableFileStorageService } from './timetable/fileStorageService';

// Create a unified database service that uses ONLY localStorage
class DatabaseService {
  // Student methods - localStorage only
  async getAllStudents() {
    return await studentsFileStorageService.getStudents();
  }

  async getStudent(id: number) {
    const students = await studentsFileStorageService.getStudents();
    return students.find(student => student.id === id);
  }

  async addStudent(student: any) {
    return await studentsFileStorageService.addStudent(student);
  }

  async updateStudent(id: number, studentData: any) {
    return await studentsFileStorageService.updateStudent(id, studentData);
  }

  async deleteStudent(id: number) {
    return await studentsFileStorageService.deleteStudent(id);
  }

  async searchStudents(query: string) {
    const students = await studentsFileStorageService.getStudents();
    return students.filter(student => 
      student.name?.toLowerCase().includes(query.toLowerCase()) ||
      student.surname?.toLowerCase().includes(query.toLowerCase()) ||
      student.email?.toLowerCase().includes(query.toLowerCase()) ||
      student.contactNumber?.toLowerCase().includes(query.toLowerCase())
    );
  }

  // Teacher methods - localStorage only
  async getAllTeachers() {
    return await teachersFileStorageService.getAllTeachers();
  }

  async getTeacher(id: string) {
    return await teachersFileStorageService.getTeacherById(id);
  }

  async addTeacher(teacher: any) {
    return await teachersFileStorageService.addTeacher(teacher);
  }

  async updateTeacher(id: string, teacherData: any) {
    return await teachersFileStorageService.updateTeacher(id, teacherData);
  }

  async deleteTeacher(id: string) {
    return await teachersFileStorageService.deleteTeacher(id);
  }

  async searchTeachers(query: string) {
    return await teachersFileStorageService.searchTeachers(query);
  }

  // Admin methods - localStorage only
  async getAllAdmins() {
    return await adminFileStorageService.getAllAdmins();
  }

  async getAdmin(id: string) {
    return await adminFileStorageService.getAdminById(id);
  }

  async addAdmin(admin: any) {
    return await adminFileStorageService.addAdmin(admin);
  }

  async updateAdmin(id: string, adminData: any) {
    return await adminFileStorageService.updateAdmin(id, adminData);
  }

  async deleteAdmin(id: string) {
    await adminFileStorageService.deleteAdmin(id);
    return true;
  }

  async searchAdmins(query: string) {
    const admins = await adminFileStorageService.getAllAdmins();
    return admins.filter(admin => 
      admin.firstName?.toLowerCase().includes(query.toLowerCase()) ||
      admin.lastName?.toLowerCase().includes(query.toLowerCase()) ||
      admin.email?.toLowerCase().includes(query.toLowerCase()) ||
      admin.position?.toLowerCase().includes(query.toLowerCase())
    );
  }

  // Timetable methods - localStorage only
  async getAllTimetables() {
    return await timetableFileStorageService.getAllTimetables();
  }

  async getTimetable(id: string) {
    return await timetableFileStorageService.getTimetableById(id);
  }

  async addTimetable(timetable: any) {
    return await timetableFileStorageService.addTimetable(timetable);
  }

  async updateTimetable(id: string, timetableData: any) {
    return await timetableFileStorageService.updateTimetable(id, timetableData);
  }

  async deleteTimetable(id: string) {
    return await timetableFileStorageService.deleteTimetable(id);
  }

  // Utility methods
  async getTimetableSyncStatus() {
    return 'synced' as 'synced' | 'syncing' | 'offline';
  }

  // Storage type check - always localStorage
  getStorageType() {
    return 'localStorage';
  }

  isLocalStorageMode() {
    return true;
  }
}

export const dbService = new DatabaseService();

// Re-export storage service
export { universalStorage } from '../storage/universalStorage';
